import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate, useParams, Outlet } from "react-router-dom";
import { Spinner } from "@porchpass/ui";
import { useWebClientApi } from "src/api";
import { ProtectedProvider, useAuth, useUser } from "src/context";
import { useApiErrorHandler } from "src/hooks/useApiErrorHandler";
import { handleApplicationNavigationGuard } from "src/initializers/helpers";
import type { DtoApplication } from "@porchpass/api-client";
import { getIdsFromPath } from "src/utils";

export default () => {
  const { dealerName } = useParams();
  const { user, processMagicLink } = useAuth();
  const { userData, setUserData } = useUser();
  const { pathname: currentPath } = useLocation();
  const navigate = useNavigate();
  const [loading, setIsLoading] = useState(false);
  const api = useWebClientApi();
  const { handleApiError } = useApiErrorHandler();
  const redirectTimeout = useRef<ReturnType<typeof setTimeout> | null>();

  // Process magic link if present
  useEffect(() => {
    processMagicLink(navigate);
  }, [navigate]);

  // Authentication check
  useEffect(() => {
    if (!user || !user.accessToken) {
      redirectTimeout.current = setTimeout(() => {
        navigate(
          `/${dealerName}/login?redirect=${encodeURIComponent(currentPath)}`,
        );
      }, 500);
    }

    return () => {
      if (redirectTimeout.current) clearTimeout(redirectTimeout.current);
    };
  }, [user]);

  // Application navigation guard
  useEffect(() => {
    const { application_id } = getIdsFromPath();
    if (
      userData?.application_id &&
      application_id &&
      application_id !== userData?.application_id &&
      user
    ) {
      const handleApplicationSuccess = (data: DtoApplication) =>
        handleApplicationNavigationGuard(data, {
          firebaseUser: user,
          setUserData,
        });
      const handleApplicationError = (error: Error) => {
        handleApiError(error);
        console.error(`Failed to fetch application: ${application_id}`, error);
      };

      setIsLoading(true);
      setUserData?.({ dealer_id: dealerName });

      const fetchApplicationById = async () => {
        try {
          const data = await api.getApplication(application_id);
          handleApplicationSuccess(data);
        } catch (error) {
          handleApplicationError(error as Error);
        } finally {
          setIsLoading(false);
        }
      };
      void fetchApplicationById();
    }
  }, [currentPath]);

  if (loading) {
    return (
      <div className="flex flex-col items-center gap-4 p-5">
        <Spinner size="l" />
      </div>
    );
  }

  return user && user.accessToken ? (
    <ProtectedProvider>
      <Outlet />
    </ProtectedProvider>
  ) : null;
};
